import React, { useState, useEffect } from 'react';
import { 
  X, 
  Loader2, 
  Save, 
  Package, 
  FileText, 
  Layers, 
  IndianRupee, 
  Percent, 
  Briefcase, 
  PlusSquare, 
  Type,
  Edit,
  Hash,
  Calculator,
  Plus
} from 'lucide-react';
import styles from './WorkOrderDrawer.module.css';
import api from '../../api/axios';
import CustomSelect from '../Common/Select/CustomSelect';
import toast from 'react-hot-toast';

const WorkOrderDrawer = ({ isOpen, onClose, onSave, mode = 'add', initialData = null }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    project_id: '',
    itemName: '',
    description: '',
    quantity: 1,
    unit_id: '',
    salesPrice: 0,
    tax_id: '',
    hsnCode: ''
  });

  const [projects, setProjects] = useState([]);
  const [units, setUnits] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [fetchingItem, setFetchingItem] = useState(false);

  const [isUnitModalOpen, setIsUnitModalOpen] = useState(false);
  const [newUnit, setNewUnit] = useState({ unit_name: '' });
  
  const [isTaxModalOpen, setIsTaxModalOpen] = useState(false);
  const [newTax, setNewTax] = useState({ tax_name: '', tax_percentage: '' });
  
  const [savingModal, setSavingModal] = useState(false);

  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const [projRes, unitRes, taxRes] = await Promise.all([
          api.get('/projects-dropdown'),
          api.get('/units-dropdown'),
          api.get('/taxes-dropdown')
        ]);
        if (projRes.data.success) setProjects(projRes.data.data);
        if (unitRes.data.success) setUnits(unitRes.data.data);
        if (taxRes.data.success) setTaxes(taxRes.data.data);
      } catch (error) {
        console.error('Error fetching dropdown data:', error);
      }
    };
    if (isOpen) {
      fetchDropdownData();
      if (mode === 'edit' && initialData?.id) {
        fetchWorkOrderItemDetail(initialData.id);
      } else {
        resetForm();
      }
    }
  }, [isOpen, mode, initialData]);

  const fetchWorkOrderItemDetail = async (id) => {
    setFetchingItem(true);
    try {
      const response = await api.get(`/work-order-items/${id}`);
      if (response.data.success) {
        const item = response.data.data;
        setFormData({
          project_id: item.project_id || '',
          itemName: item.item_name || '',
          description: item.description || '',
          quantity: item.quantity || 0,
          unit_id: item.unit_id || '',
          salesPrice: typeof item.sales_price === 'string' ? parseFloat(item.sales_price.replace(/[^0-9.]/g, '')) : item.sales_price || 0,
          tax_id: item.tax_id || '',
          hsnCode: item.hsn_code || ''
        });
      }
    } catch (error) {
      console.error('Error fetching work order item detail:', error);
      toast.error('Failed to fetch item details');
    } finally {
      setFetchingItem(false);
    }
  };

  const resetForm = () => {
    setFormData({
      project_id: '',
      itemName: '',
      description: '',
      quantity: 1,
      unit_id: '',
      salesPrice: 0,
      tax_id: '',
      hsnCode: ''
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'quantity' || name === 'salesPrice' ? parseFloat(value) || 0 : value
    }));
  };

  const totalAmount = (formData.quantity * formData.salesPrice).toLocaleString('en-IN', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2
  });

  const handleSubmit = async (shouldAddAnother = false) => {


    setIsSubmitting(true);
    try {
      const payload = {
        project_id: formData.project_id,
        item_name: formData.itemName,
        description: formData.description,
        quantity: formData.quantity,
        unit_id: formData.unit_id,
        sales_price: formData.salesPrice,
        tax_id: formData.tax_id,
        hsn_code: formData.hsnCode
      };

      let response;
      if (mode === 'edit') {
        response = await api.put(`/work-order-items/${initialData.id}`, payload);
      } else {
        response = await api.post('/work-order-items', payload);
      }

      if (response.data.success) {
        toast.success(`Work order item ${mode === 'edit' ? 'updated' : 'created'} successfully`);
        if (onSave) onSave();

        if (shouldAddAnother) {
          setFormData({
            project_id: formData.project_id, // Keep project for convenience
            itemName: '',
            description: '',
            quantity: 1,
            unit_id: '',
            salesPrice: 0,
            tax_id: '',
            hsnCode: ''
          });
        } else {
          onClose();
        }
      }
    } catch (error) {
      console.error(`Error ${mode === 'edit' ? 'updating' : 'creating'} work order item:`, error);
      toast.error(error.response?.data?.message || `Failed to ${mode === 'edit' ? 'update' : 'create'} work order item`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveUnit = async (e) => {
    e.preventDefault();
    if (!newUnit.unit_name) {
      toast.error("Unit name is required");
      return;
    }
    setSavingModal(true);
    try {
      const res = await api.post('/units', newUnit);
      if (res.data.success) {
        const addedUnit = { value: res.data.data.id, label: res.data.data.unit_name };
        setUnits(prev => [...prev, res.data.data]);
        setFormData(prev => ({ ...prev, unit_id: addedUnit.value }));
        toast.success('Unit added successfully');
        setIsUnitModalOpen(false);
        setNewUnit({ unit_name: '' });
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add unit');
    } finally {
      setSavingModal(false);
    }
  };

  const handleSaveTax = async (e) => {
    e.preventDefault();
    if (!newTax.tax_name || newTax.tax_percentage === '') {
      toast.error("Tax name and percentage are required");
      return;
    }
    setSavingModal(true);
    try {
      const res = await api.post('/taxes', newTax);
      if (res.data.success) {
        const addedTax = { value: res.data.data.id, label: `${res.data.data.tax_name} (${res.data.data.tax_percentage}%)` };
        setTaxes(prev => [...prev, res.data.data]);
        setFormData(prev => ({ ...prev, tax_id: addedTax.value }));
        toast.success('Tax added successfully');
        setIsTaxModalOpen(false);
        setNewTax({ tax_name: '', tax_percentage: '' });
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add tax');
    } finally {
      setSavingModal(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={`${styles['drawer-overlay-work-order']} ${isOpen ? styles['open'] : ''}`} onClick={onClose}>
      <div className={`${styles['drawer-content']} ${isOpen ? styles['open'] : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className={styles['drawer-header']}>
          <div className={styles['header-title-wrapper']}>
            <h2>{mode === 'add' ? 'Add Work Order Item' : 'Edit Work Order Item'}</h2>
            <p>{mode === 'add' ? 'Create a new item specification' : 'Modify existing item details'}</p>
          </div>
          <button className={styles['close-btn']} onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <div className={styles['drawer-form']}>
          {fetchingItem ? (
            <div className={styles['drawer-loading']}>
              <div className={styles['spinner']}></div>
              <p>Fetching item details...</p>
            </div>
          ) : (
            <div className={styles['form-scroll-area']}>
              <div className={styles['form-group']}>
                <label><Briefcase size={16} /> Project <span className={styles['required-star']}>*</span></label>
                <CustomSelect
                  value={formData.project_id ? {
                    value: formData.project_id,
                    label: projects.find(p => p.id === formData.project_id)?.project_name || 'Select Project'
                  } : null}
                  onChange={(opt) => setFormData(prev => ({ ...prev, project_id: opt ? opt.value : '' }))}
                  options={projects.map(p => ({
                    value: p.id,
                    label: `${p.project_name} (${p.project_code})`
                  }))}
                  placeholder="Search and select project..."
                  isClearable={true}
                  isSearchable={true}
                  required
                />
              </div>

              <div className={styles['form-group']}>
                <label><Type size={16} /> Item Name <span className={styles['required-star']}>*</span></label>
                <input
                  type="text"
                  name="itemName"
                  placeholder="e.g. Copper Wire - 12 Gauge"
                  value={formData.itemName}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className={styles['form-group']}>
                <label><Hash size={16} /> HSN Code</label>
                <input
                  type="text"
                  name="hsnCode"
                  placeholder="e.g. 8481"
                  value={formData.hsnCode || ''}
                  onChange={handleInputChange}
                  maxLength="8"
                />
              </div>

              <div className={styles['form-group']}>
                <label><FileText size={16} /> Description</label>
                <textarea
                  name="description"
                  placeholder="Enter technical specifications or notes..."
                  rows="4"
                  value={formData.description}
                  onChange={handleInputChange}
                ></textarea>
              </div>

              <div className={styles['form-row']}>
                <div className={styles['form-group']}>
                  <label><Hash size={16} /> Quantity <span className={styles['required-star']}>*</span></label>
                  <input
                    type="number"
                    name="quantity"
                    value={formData.quantity}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className={styles['form-group']}>
                  <label><Layers size={16} /> Unit <span className={styles['required-star']}>*</span></label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <div style={{ flex: 1 }}>
                      <CustomSelect
                        value={formData.unit_id ? {
                          value: formData.unit_id,
                          label: units.find(u => u.id === formData.unit_id)?.unit_name || 'Select Unit'
                        } : null}
                        onChange={(opt) => setFormData(prev => ({ ...prev, unit_id: opt ? opt.value : '' }))}
                        options={units.map(u => ({
                          value: u.id,
                          label: u.unit_name
                        }))}
                        placeholder="Select unit"
                        isSearchable={true}
                        required
                      />
                    </div>
                    <button 
                        type="button" 
                        onClick={() => setIsUnitModalOpen(true)}
                        style={{ padding: '8px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px', color: '#0f172a', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        title="Add New Unit"
                    >
                        <Plus size={18} />
                    </button>
                  </div>
                </div>
              </div>

              <div className={styles['form-row']}>
                <div className={styles['form-group']}>
                  <label><IndianRupee size={16} /> Sales Price <span className={styles['required-star']}>*</span></label>
                  <input
                    type="number"
                    name="salesPrice"
                    placeholder="0.00"
                    value={formData.salesPrice || ''}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className={styles['form-group']}>
                  <label><Percent size={16} /> Tax</label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <div style={{ flex: 1 }}>
                      <CustomSelect
                        value={formData.tax_id ? {
                          value: formData.tax_id,
                          label: taxes.find(t => t.id === formData.tax_id)?.tax_name || 'Select Tax'
                        } : null}
                        onChange={(opt) => setFormData(prev => ({ ...prev, tax_id: opt ? opt.value : '' }))}
                        options={taxes.map(t => ({
                          value: t.id,
                          label: `${t.tax_name} (${t.tax_percentage}%)`
                        }))}
                        placeholder="Select tax"
                        isSearchable={true}
                        isClearable={true}
                      />
                    </div>
                    <button 
                        type="button" 
                        onClick={() => setIsTaxModalOpen(true)}
                        style={{ padding: '8px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px', color: '#0f172a', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        title="Add New Tax"
                    >
                        <Plus size={18} />
                    </button>
                  </div>
                </div>
              </div>

              <div className={styles['auto-total-box']}>
                <div className={styles['total-header']}>
                  <div className={styles['total-title-group']}>
                    <Calculator size={16} className={styles['total-icon']} />
                    <span className={styles['total-label']}>AUTO-CALCULATED TOTAL</span>
                  </div>
                  <span className={styles['formula']}>Quantity × Sales Price</span>
                </div>
                <div className={styles['total-value-row']}>
                  <span className={styles['total-value']}>₹ {totalAmount}</span>
                </div>
              </div>
            </div>
          )}

          <div className={styles['drawer-footer']}>
            <button type="button" className={styles['cancel-btn']} onClick={onClose} disabled={isSubmitting}>
              Cancel
            </button>
            <div className={styles['footer-actions']}>
              {mode === 'add' && (
                <button
                  type="button"
                  className={styles['save-another-btn']}
                  onClick={() => handleSubmit(true)}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? <Loader2 className={styles['spinner']} size={18} /> : <PlusSquare size={18} />}
                  <span>Save & Add Another</span>
                </button>
              )}
              <button
                type="button"
                className={styles['save-btn']}
                onClick={() => handleSubmit(false)}
                disabled={isSubmitting}
              >
                {isSubmitting ? <Loader2 className={styles['spinner']} size={18} /> : <Save size={18} />}
                <span>{mode === 'add' ? 'Save Item' : 'Update Item'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {isUnitModalOpen && (
          <div className={styles['modal-overlay']} onClick={() => setIsUnitModalOpen(false)}>
              <div className={styles['modal-content']} onClick={(e) => e.stopPropagation()}>
                  <div className={styles['modal-header']}>
                      <h3>Add Unit</h3>
                      <button className={styles['close-btn']} onClick={() => setIsUnitModalOpen(false)}>
                          <X size={20} />
                      </button>
                  </div>
                  <form onSubmit={handleSaveUnit}>
                      <div className={styles['modal-body']}>
                          <div className={styles['form-group']}>
                              <label>Unit Name <span className={styles['required-star']}>*</span></label>
                              <input
                                  type="text"
                                  value={newUnit.unit_name}
                                  onChange={(e) => setNewUnit({ ...newUnit, unit_name: e.target.value })}
                                  placeholder="e.g. Kg, Mtr, Nos"
                                  required
                                  style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #e2e8f0' }}
                              />
                          </div>
                      </div>
                      <div className={styles['modal-footer']}>
                          <button type="button" className={styles['btn-cancel']} onClick={() => setIsUnitModalOpen(false)} disabled={savingModal}>
                              Cancel
                          </button>
                          <button type="submit" className={styles['btn-upload']} disabled={savingModal}>
                              {savingModal ? <Loader2 size={18} className={styles['spinner']} /> : <Save size={18} />}
                              <span>Save</span>
                          </button>
                      </div>
                  </form>
              </div>
          </div>
      )}

      {isTaxModalOpen && (
          <div className={styles['modal-overlay']} onClick={() => setIsTaxModalOpen(false)}>
              <div className={styles['modal-content']} onClick={(e) => e.stopPropagation()}>
                  <div className={styles['modal-header']}>
                      <h3>Add Tax</h3>
                      <button className={styles['close-btn']} onClick={() => setIsTaxModalOpen(false)}>
                          <X size={20} />
                      </button>
                  </div>
                  <form onSubmit={handleSaveTax}>
                      <div className={styles['modal-body']}>
                          <div className={styles['form-group']}>
                              <label>Tax Name <span className={styles['required-star']}>*</span></label>
                              <input
                                  type="text"
                                  value={newTax.tax_name}
                                  onChange={(e) => setNewTax({ ...newTax, tax_name: e.target.value })}
                                  placeholder="e.g. GST 18%"
                                  required
                                  style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #e2e8f0' }}
                              />
                          </div>
                          <div className={styles['form-group']}>
                              <label>Tax Percentage (%) <span className={styles['required-star']}>*</span></label>
                              <input
                                  type="number"
                                  value={newTax.tax_percentage}
                                  onChange={(e) => setNewTax({ ...newTax, tax_percentage: e.target.value })}
                                  placeholder="e.g. 18"
                                  required
                                  step="0.01"
                                  min="0"
                                  max="100"
                                  style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #e2e8f0' }}
                              />
                          </div>
                      </div>
                      <div className={styles['modal-footer']}>
                          <button type="button" className={styles['btn-cancel']} onClick={() => setIsTaxModalOpen(false)} disabled={savingModal}>
                              Cancel
                          </button>
                          <button type="submit" className={styles['btn-upload']} disabled={savingModal}>
                              {savingModal ? <Loader2 size={18} className={styles['spinner']} /> : <Save size={18} />}
                              <span>Save</span>
                          </button>
                      </div>
                  </form>
              </div>
          </div>
      )}
    </div>
  );
};

export default WorkOrderDrawer;
